import React, { useState, ReactNode, ErrorInfo, useEffect, lazy, Suspense } from 'react';

// The admin panel is the only thing that needs Firebase Auth and Storage, and
// it is ~290KB of UI a visitor never sees. Loading it lazily keeps both the
// bundle and the auth iframe (and its 33 Google cookies) off the public site.
const Admin = lazy(() => import('./components/Admin'));
import SiteV2 from './components/v2/SiteV2';
import { markOwner } from './analytics';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-paper text-ink p-8 font-grotesk text-center">
          <div>
            <h1 className="text-xl text-electric mb-4">Something went wrong.</h1>
            <p className="opacity-70">{this.state.error?.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-8 px-6 py-3 rounded-full border border-ink/20 hover:bg-ink hover:text-[#E9EAF0] transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children || null;
  }
}

// Meta Tag Manager
//
// index.html is the single source of truth for SEO and social metadata.
//
// It has to be: social and chat scrapers (Facebook, LinkedIn, Slack, iMessage,
// WhatsApp, Discord, X) never execute JavaScript, so a value written here can
// never influence a shared link's preview. It only reaches Google's renderer.
//
// Keeping a second copy in Firestore meant the two silently diverged: the
// stored seoTitle ("Hamlet Hayrapetyan | Motion Designer") was overwriting the
// hand-tuned static title on every load, and the stored seoImage pointed at a
// deleted Storage object that 404'd. Both were live regressions that the admin
// panel gave no indication of. So the runtime no longer overrides content that
// index.html already states correctly.
//
// All this still does is pin og:url. The previous code set it to
// window.location.href, which made every preview channel and ?query variant
// advertise itself as the canonical page.
const CANONICAL_URL = 'https://motion.hamlt.com/';

const MetaManager = () => {
    useEffect(() => {
        const tag = document.querySelector('meta[property="og:url"]');
        if (tag) tag.setAttribute('content', CANONICAL_URL);
    }, []);
    return null;
};

function App() {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkRoute = () => {
        const isHashAdmin = window.location.hash === '#/admin';
        const isPathAdmin = window.location.pathname === '/admin';
        setIsAdmin(isHashAdmin || isPathAdmin);
        if (isHashAdmin || isPathAdmin) markOwner();
    };

    checkRoute();
    window.addEventListener('hashchange', checkRoute);
    window.addEventListener('popstate', checkRoute);

    return () => {
        window.removeEventListener('hashchange', checkRoute);
        window.removeEventListener('popstate', checkRoute);
    };
  }, []);

  // robots.txt can block /admin, but it cannot block the #/admin hash route:
  // fragments are never sent to the server. Flip the robots meta at runtime so
  // the panel stays out of the index either way.
  useEffect(() => {
    const tag = document.querySelector('meta[name="robots"]');
    if (!tag) return;
    const indexable = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    tag.setAttribute('content', isAdmin ? 'noindex, nofollow' : indexable);
  }, [isAdmin]);

  return (
    <ErrorBoundary>
      <MetaManager />
      {isAdmin ? (
        <Suspense fallback={<div className="min-h-screen bg-paper" />}>
          <Admin />
        </Suspense>
      ) : (
        <SiteV2 />
      )}
    </ErrorBoundary>
  );
}

export default App;
